/**
 * Sidebar Component
 * Feature: 012-desktop-mobile-shells
 * User Story: US2 - Consistent Feature Access
 *
 * Desktop sidebar navigation component.
 * Full implementation in Phase 4 (US2).
 */

import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { map, Observable, startWith, take } from 'rxjs';
import { getDesktopSidebarItems, NAVIGATION_CONFIG } from '../../../core/constants/navigation.constants';
import { NavItem } from '../../../core/domain/shell';
import { UserService } from '../../../core/services/user.service';
import { PatientService } from '../../../core/services/patient.service';
import { silent5xxContext } from '../../../core/interceptors/http-context.tokens';
import { MessageUnreadService } from '../../../features/messages/shared/message-unread.service';
import { isMessagesContentEnabled } from '../../../features/messages/shared/messages-feature.util';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    MatIconModule,
    MatListModule
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss'
})
export class SidebarComponent implements OnInit {
  private userService = inject(UserService);
  private patientService = inject(PatientService);
  private unreadService = inject(MessageUnreadService);

  /** Live unread message count for the Messages nav badge. */
  unreadCount$: Observable<number> = this.unreadService.unreadCount$;

  /** Observable for patient count to trigger re-render */
  patientCount$: Observable<number> = this.patientService.patients$.pipe(
    map(patients => patients?.length ?? 0),
    startWith(0)
  );

  ngOnInit(): void {
    // Ensure patients are loaded for singular/plural label logic
    // Use skipGlobalLoading to avoid triggering the full-screen overlay
    const currentPatients = (this.patientService as any).patientsSubject?.value;
    if (!currentPatients || currentPatients.length === 0) {
      this.patientService.getPatients(true, silent5xxContext()).pipe(take(1)).subscribe();
    }
  }

  /**
   * Get navigation items filtered for desktop sidebar.
   * Also checks feature flags for conditional items.
   * Feature 013-shell-navigation: Respects showWhenDisabled property.
   * - If item has no requiredFeature: always show
   * - If item has requiredFeature AND feature is enabled: show
   * - If item has requiredFeature AND feature is disabled:
   *   - If showWhenDisabled is true: show (route will handle placeholder)
   *   - If showWhenDisabled is false/undefined: hide
   */
  get navItems(): NavItem[] {
    return getDesktopSidebarItems().filter(item => {
      // Items without requiredFeature are always shown
      if (!item.requiredFeature) {
        return true;
      }

      // Check if the required feature is enabled
      const isEnabled = this.userService.isFeatureEnabled(item.requiredFeature);

      if (isEnabled) {
        // Feature is enabled, show the item
        return true;
      }

      // Feature is disabled - check showWhenDisabled
      // If showWhenDisabled is true, show the item (route will display placeholder)
      return item.showWhenDisabled === true;
    });
  }

  /**
   * Whether the unread badge may show for this nav item: only the Messages item
   * (badgeKey 'messages-unread') and only when the Messages content is actually
   * enabled for the user (real flag or local-dev override). Placeholder/flag-off
   * users never see a badge. The numeric count gate (> 0) is applied in the
   * template via the async unread count.
   */
  showsUnreadBadge(item: NavItem): boolean {
    return (
      item.badgeKey === 'messages-unread' &&
      isMessagesContentEnabled(this.userService)
    );
  }

  /** Display string for the badge count (caps at 99+). */
  badgeLabel(count: number): string {
    return count > 99 ? '99+' : `${count}`;
  }

  /**
   * Get the label key for a nav item.
   * Uses singular form for patients route by default.
   * Only uses plural when more than 1 patient.
   */
  getLabelKey(item: NavItem): string {
    if (item.labelKeySingular && item.route === '/patients') {
      // Use synchronous value from BehaviorSubject
      const patientCount = (this.patientService as any).patientsSubject?.value?.length ?? 0;
      // Use plural only when > 1 patient
      if (patientCount > 1) {
        return item.labelKey; // Plural: "Patients"
      }
      return item.labelKeySingular; // Singular: "Patient" (default for 0 or 1)
    }
    return item.labelKey;
  }
}
